import { useEffect, useState } from 'react';
import {
  Button,
  Choice,
  Dialog,
  Field,
  Icon,
  Input,
  useErrorDialog,
  useToast,
} from '@dayon/design-system';
import { formatCount } from '@dayon/money';

import { api, ApiRequestError } from '../../lib/api.ts';
import type { BrandRow, BrandWriteResult } from './types.ts';

/**
 * Edit a brand: its name, its host, and whether it is on the floor.
 *
 * TWO OF THESE THREE ARE CONSEQUENTIAL and the dialog says so rather than
 * presenting three equivalent inputs:
 *
 *   · THE HOST is the tenancy boundary. Moving it means the old host stops
 *     resolving — every player arriving there is told no brand is served at
 *     that address — and the new one starts. Nothing migrates; the players
 *     already attributed to this brand stay attributed to it.
 *   · ACTIVE is not a display flag. The player portal's resolver ends with
 *     `AND active`, so switching a brand off refuses every registration on its
 *     host and every sign-in by its existing players. The count of players that
 *     affects is shown before the switch is thrown, and it comes back from the
 *     server rather than being guessed here.
 *
 * The licence lives in its own dialog. It is a different act, it is the one
 * this screen exists for, and folding it in here would bury it.
 */

const HOST = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/;

export function EditBrandDialog({
  brand,
  onClose,
  onDone,
}: {
  brand: BrandRow | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const { showError } = useErrorDialog();
  const { toast } = useToast();

  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [active, setActive] = useState(true);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setName(brand?.name ?? '');
    setDomain(brand?.domain ?? '');
    setActive(brand?.active ?? true);
    setFields({});
  }, [brand]);

  const close = () => {
    setFields({});
    onClose();
  };

  const players = brand ? Number(brand.players) : 0;
  const deactivating = brand !== null && brand.active && !active;
  const hostMoving = brand !== null && domain.trim().toLowerCase() !== brand.domain;

  const submit = async () => {
    if (!brand) return;
    const host = domain.trim().toLowerCase().replace(/\.$/, '');
    const problems: Record<string, string> = {};
    if (name.trim().length < 2) problems['name'] = 'Give the brand a name.';
    if (!HOST.test(host)) {
      problems['domain'] =
        'The bare host only — peryaplay.ph. No https://, no port, no path, and at least one dot.';
    }
    if (Object.keys(problems).length > 0) {
      setFields(problems);
      return;
    }

    /* Only what CHANGED. The route refuses an empty PATCH rather than writing an
       audit row that records nothing, and sending every field on every save
       would make the trail unreadable — every entry would look like a full
       rewrite of the brand. */
    const body: Record<string, unknown> = {};
    if (name.trim() !== brand.name) body['name'] = name.trim();
    if (host !== brand.domain) body['domain'] = host;
    if (active !== brand.active) body['active'] = active;
    if (Object.keys(body).length === 0) {
      setFields({ name: 'Nothing has been changed yet.' });
      return;
    }

    setBusy(true);
    try {
      const result = await api.patch<BrandWriteResult>(`/api/platform/brands/${brand.id}`, body);
      toast(
        result.active
          ? `${result.ref} saved — ${result.domain} resolves to it.`
          : `${result.ref} taken off the floor. ${formatCount(result.affectedPlayers ?? 0)} players can no longer sign in.`,
      );
      onDone();
      close();
    } catch (error: unknown) {
      if (error instanceof ApiRequestError && error.fields) {
        setFields(error.fields);
        return;
      }
      showError({
        title: 'Could not save the brand',
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'Nothing was changed. Try again shortly.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={brand !== null}
      onClose={close}
      wide
      title={brand ? `Edit ${brand.name}` : 'Edit brand'}
      actions={
        <>
          <Button onClick={close}>Cancel</Button>
          <Button variant="primary" busy={busy} onClick={() => void submit()}>
            Save changes
          </Button>
        </>
      }
    >
      <div className="stack">
        <Field label="Brand name" error={fields['name']}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              aria-invalid={invalid}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          )}
        </Field>

        <Field
          label="Host"
          error={fields['domain']}
          hint="One host, one brand. The platform refuses a host another brand already serves, whatever its capitalisation."
        >
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              aria-invalid={invalid}
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              inputMode="url"
              autoCapitalize="none"
              spellCheck={false}
            />
          )}
        </Field>

        {hostMoving && brand ? (
          <div className="notice">
            <Icon name="warning-circle" />
            <div>
              Moving the host means <span className="tabular">{brand.domain}</span> stops resolving
              to any brand — a player arriving there is told no brand is served at that address —
              and <span className="tabular">{domain.trim().toLowerCase()}</span> starts. The{' '}
              {formatCount(players)} players already attributed to {brand.name} stay attributed to
              it; nothing about them moves.
            </div>
          </div>
        ) : null}

        <Field label="On the floor">
          {({ id }) => (
            <Choice
              id={id}
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              label="Live — the portal serves this brand and its players can sign in"
            />
          )}
        </Field>

        {deactivating && brand ? (
          <div className="notice">
            <Icon name="warning-circle" />
            <div>
              Taking {brand.name} off the floor refuses <strong>every</strong> registration on{' '}
              <span className="tabular">{brand.domain}</span> and stops its{' '}
              {formatCount(players)} existing players signing in — the player site answers only for
              a brand that is on the floor. Nothing is deleted and nothing
              is refunded; the brand can be put back on the floor from this same dialog.
            </div>
          </div>
        ) : null}

        <div className="notice notice-quiet">
          <Icon name="info" />
          <div>
            The reference <span className="tabular">{brand?.ref}</span> cannot be changed — every
            record that quotes it would be quoting something that no longer exists. The licence is
            renewed from its own control on this screen.
          </div>
        </div>
      </div>
    </Dialog>
  );
}
